<template>
  <div
    v-if="!isClosed"
    :class="[
      'relative flex w-full items-center text-primary-purple',
      'lg:top-0',
      hasHolidayPromo
        ? 'bg-primary-green py-2 md:justify-center lg:py-[9px] 3xl:py-[14px]'
        : 'bg justify-center bg-primary-beige py-[13px] lg:py-[22px]',
    ]"
  >
    <p
      class="flex items-center text-center lg:mx-2 lg:flex-row"
      :class="[!hasHolidayPromo && 'mx-8 flex-col', hasHolidayPromo && 'mx-4']"
    >
      <i
        v-show="hasHolidayPromo"
        class="fa-solid fa-gift mr-2 text-sm text-primary-purple md:text-base xl:text-lg"
      />
      <span
        class="hidden lg:mr-2 lg:inline-block"
        :class="hasHolidayPromo ? 'font-medium md:mr-2 md:inline-block xl:text-2xl' : 'font-bold'"
      >
        {{ welcome }}
      </span>
      <span
        class="inline-flex items-center lg:mt-0 lg:flex-row"
        :class="[
          hasHolidayPromo && 'font-medium xl:text-2xl',
          !hasHolidayPromo && 'mt-2 flex-col',
        ]"
      >
        <span>
          <span
            v-if="$store.state.user.first_name"
            class="lg:hidden"
            :class="hasHolidayPromo && 'hidden'"
          >{{ $store.state.user.first_name }},</span>
          <span>{{ title[0] }}</span>
          <span
            v-if="title[1]"
            class="block sm:inline-block"
          >
            {{ title[1] }}
          </span>
        </span>
        <a
          v-if="hasHolidayPromo"
          class="al-btn al-btn--primary-dark ml-0 hidden px-3 text-xs font-medium md:ml-4 md:flex"
          :class="'md:h-7 md:px-4 md:text-sm lg:ml-8 lg:h-10 xl:ml-10 xl:px-9 3xl:ml-12 3xl:h-10 3xl:text-lg'"
          :href="ctaLink"
          @click="trackHolidayReactivateEvent"
        >
          Get Everything
        </a>
        <button
          v-else
          class="al-btn al-btn--primary-dark mt-5 h-8 rounded-lg px-8 py-0 text-xs font-bold lg:ml-3 lg:mt-0"
          @click="redirectToRespectivePlan"
        >
          Reactivate Your Plan
        </button>
      </span>
    </p>`
    <button
      class="absolute right-0 top-0 mr-4 mt-2 text-lg lg:top-auto lg:mr-5 lg:mt-0"
      aria-label="close"
      @click="close"
    >
      <i class="fal fa-times" />
    </button>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import { COOKIES, USER_PLAN, USER_PLAN_URLS } from '@/constants';
import getContext from '@/utilities/get-context';
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';

export default {
  inject: ['hasHolidayPromo'],
  data() {
    return {
      isClosed: true,
    };
  },
  computed: {
    welcome() {
      return `Welcome back, ${this.$store.state.user.first_name}!`;
    },
    title() {
      if (this.hasHolidayPromo) {
        return ['Get 10% off the Everything plan'];
      }

      return ["Everything's just where you left it.", 'Ready to get started?'];
    },
    stripText() {
      return `${this.welcome} ${this.title.join(' ')}`;
    },
    plan() {
      if (
        getContext().__MA__.billing_engine_subscription?.subscriptionReactivation
          ?.lastPlanPeriodUnit === 'year'
      ) {
        return 'Annual';
      }
      return 'Monthly';
    },
    ctaLink() {
      return this.hasHolidayPromo ? `/pricing/${USER_PLAN_URLS[USER_PLAN.EVERYTHING_YEARLY]}/` : '/reactivate';
    },
  },
  watch: {
    isClosed(val) {
      this.$store.dispatch('setIsReactivationPromoVisible', !val);
    },
  },
  mounted() {
    this.isClosed = !getContext().__MA__.billing_engine_subscription?.subscriptionReactivation?.showBanner
      || !!Cookies.get(COOKIES.NAMES.REACTIVATION_BAR_COOKIE);

    if (!this.isClosed) {
      eventTracker(analyticsEvents.headerStripViewed, {
        strip_name: 'reactivation',
        strip_text: this.stripText,
        flow: 'Reactivation flow',
        plan: this.plan,
      });
      if (this.hasHolidayPromo) {
        eventTracker(analyticsEvents.reactivationBannerView, {
          banner_type: 'sticky',
          button_name: 'Get Everything',
          banner_title: 'Get 10% off the Everything plan',
        });
      }
    }
  },
  methods: {
    close() {
      this.isClosed = true;
      Cookies.set(COOKIES.NAMES.REACTIVATION_BAR_COOKIE, '1', {
        expires: 60,
      });

      eventTracker(analyticsEvents.closeButtonClicked, {
        click_source: 'reactivation strip',
        button_name: 'Reactivate your plan',
        plan: this.plan,
        flow: 'Reactivation flow',
      });
      if (this.hasHolidayPromo) {
        eventTracker(analyticsEvents.reactivationBannerClosed, {
          banner_type: 'sticky',
          button_name: 'x',
          banner_title: 'Get 10% off the Everything plan',
        });
      }
    },

    redirectToRespectivePlan() {
      this.trackReactivateLinkEvent();
      window.location.href = '/pricing/annual/?reactivation_page=1';
    },

    trackReactivateLinkEvent() {
      eventTracker(analyticsEvents.buttonClicked, {
        click_source: 'reactivation strip',
        button_name: 'Reactivate your plan',
        is_valid: true,
        flow: 'Reactivation flow',
        plan: this.plan,
      });
    },
    trackHolidayReactivateEvent() {
      eventTracker(analyticsEvents.reactivationBannerClicked, {
        banner_type: 'sticky',
        button_name: 'Get Everything',
        banner_title: 'Get 10% off the Everything plan',
      });
    },
  },
};
</script>

<style scoped>
.bg {
  background-image: url('/assets/images/site/reactivation-mobile-bg-left.png'),
    url('/assets/images/site/reactivation-mobile-bg-right.png');
  background-position: top left, bottom right;
  background-repeat: no-repeat;
}

@screen lg {
  .bg {
    background-image: url('/assets/images/site/reactivation-bg.png');
    background-size: cover;
    background-position: top left;
  }
}
</style>
